// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState, useEffect } from 'react'
import { useNavigate }   from 'react-router-dom'
import { Users, Download, Printer, ArrowLeft, Phone, CreditCard, Building, Heart, Smartphone } from 'lucide-react'
import type { ExportData, ExportSection } from '@/lib/export/wilsappExport'
import { Card }          from '@/components/ui/Card'
import { Button }        from '@/components/ui/Button'
import { useAuthStore, useWizardStore } from '@/store'
import { getFirstProfile } from '@/services/profile'
import { loadNotarial } from '@/services/notarial'
import { loadPartner, loadChildren, loadBank, loadPension, loadHousing, loadPets, loadInsurance, loadInvestments, wizardLoad, pensionFundNames, type InsuranceData, type InvestmentsData } from '@/services/wizard'
import { loadTrustedPersons, type TrustedPersonConfig } from '@/services/trustedPerson'
import { loadDocuments, loadMedicalWishes, loadFuneralWishes, loadDigitalEstate } from '@/services/wishes'
import { DOSSIER_OVERVIEW } from '@/lib/constants/routes'

interface Section { title: string; icon: React.ReactNode; items: { label: string; value: string }[] }

export function NabestaandenDossierPage() {
  const navigate   = useNavigate()
  const { userId } = useAuthStore()
  const { answers: _answers } = useWizardStore(); const answers = _answers
  const [sections, setSections] = useState<Section[]>([])
  const [isLoading,setIsLoading]= useState(true)
  const [firstName,setFirstName]= useState('')
  const [generated,setGenerated]= useState(false)
  const [exportData, setExportData] = useState<ExportData | null>(null)

  useEffect(() => {
    if (!userId) return
    async function load() {
      const [profile, notarial, partner, _children, bank, pension, housing, pets, documents, medical, funeral, insuranceData, extraLocData, vpList, investData, digitalEstate] = await Promise.all([
        getFirstProfile(), loadNotarial(userId!), loadPartner(userId!), loadChildren(userId!),
        loadBank(userId!), loadPension(userId!), loadHousing(userId!), loadPets(userId!),
        loadDocuments(userId!), loadMedicalWishes(userId!), loadFuneralWishes(userId!), loadInsurance(userId!),
        wizardLoad(userId!, 'extraLocations'),
        loadTrustedPersons(userId!),
        loadInvestments(userId!),
        loadDigitalEstate(userId!),
      ])
      if (profile) setFirstName(profile.firstName)
      // ── Telefoon & digitale toegang checklist (V41) ──
      const hasPhone = digitalEstate?.hasSmartphone === 'yes'
      const hasAuth  = digitalEstate?.authenticatorApp && digitalEstate.authenticatorApp !== 'none'
      const hasPassMgr = digitalEstate?.passwordManagerApp && digitalEstate.passwordManagerApp !== 'none'
      // V41-P2: toegangslocaties
      const hasPhoneAccess    = !!(digitalEstate?.unlockCodeLocation)
      const hasComputerAccess = !!(digitalEstate?.computerAccessLocation)

      const built: Section[] = []

      // Contactpersonen
      const contacts: { label: string; value: string }[] = []
      if (partner?.name) contacts.push({ label: 'Partner', value: `${partner.name} ${partner.lastName ?? ''}`.trim() })
      if (notarial?.executeur?.name) contacts.push({ label: 'Executeur', value: `${notarial.executeur.name} - ${notarial.executeur.phone}` })
      if (notarial?.voogdij?.name) contacts.push({ label: 'Voogd', value: `${notarial.voogdij.name} - ${notarial.voogdij.phone}` })
      if (medical?.gpName) contacts.push({ label: 'Huisarts', value: `${medical.gpName}${medical.gpContact ? ' - ' + medical.gpContact : ''}` })
      // V39: Vertrouwenspersoon(en) toevoegen aan contacten
      const typedVpList = vpList as TrustedPersonConfig[] | null
      if (typedVpList && typedVpList.length > 0) {
        typedVpList.forEach(vp => {
          const vpLabel = vp.role === 'RESERVE' ? 'Tweede vertrouwenspersoon' : 'Eerste vertrouwenspersoon'
          const vpContact = vp.email ? ` - ${vp.email}` : ''
          contacts.push({ label: vpLabel, value: `${vp.firstName}${vpContact}` })
        })
      }
      if (contacts.length) built.push({ title: 'Belangrijke contactpersonen', icon: <Phone className="h-4 w-4" />, items: contacts })

      // Documenten locaties
      if (documents.length > 0) {
        built.push({
          title: 'Waar liggen documenten',
          icon: <Building className="h-4 w-4" />,
          items: documents.slice(0, 8).map(d => ({ label: d.label, value: d.location })),
        })
      }

      // Bankrekeningen
      if (bank?.accounts?.length) {
        built.push({
          title: 'Bankrekeningen',
          icon: <CreditCard className="h-4 w-4" />,
          items: bank.accounts.map(a => {
            const name = a.bankName === 'Anders' ? (a.bankNameOther || 'Anders') : a.bankName
            const ibanShown = a.iban
              ? (a.iban.replace(/\s/g, '').length > 4 ? `•••• ${a.iban.replace(/\s/g, '').slice(-4)}` : a.iban)
              : 'IBAN niet opgegeven'
            const extra = [
              a.coHolder ? `Mederekeninghouder: ${a.coHolder}` : '',
              a.notes ? `Opmerking: ${a.notes}` : '',
            ].filter(Boolean).join(' · ')
            return { label: `${name} - ${a.accountType}`, value: extra ? `${ibanShown} · ${extra}` : ibanShown }
          }),
        })
      }

      // Pensioen
      if (pension?.hasPension === 'yes') {
        built.push({
          title: 'Pensioen',
          icon: <Building className="h-4 w-4" />,
          items: [
            { label: pensionFundNames(pension).length > 1 ? 'Pensioenfondsen' : 'Pensioenfonds', value: pensionFundNames(pension).join(', ') || 'Zie MijnPensioenoverzicht.nl' },
            { label: 'Partnerpensioen', value: pension.hasPartnerPension === 'yes' ? 'Ja' : pension.hasPartnerPension === 'no' ? 'Nee' : 'Onbekend' },
            { label: 'Wezenpensioen', value: pension.hasSurvivorPension === 'yes' ? 'Ja' : pension.hasSurvivorPension === 'no' ? 'Nee' : 'Onbekend' },
            ...(pension.hasPensionOverview ? [{ label: 'Pensioenoverzicht opgeslagen', value: pension.hasPensionOverview === 'yes' ? 'Ja' : pension.hasPensionOverview === 'no' ? 'Nee' : 'Onbekend' }] : []),
            ...((pension.pensionOverviewLocation ?? '').trim() ? [{ label: 'Pensioenoverzicht te vinden', value: (pension.pensionOverviewLocation ?? '').trim() }] : []),
            ...(pension.hasSupplementaryPension === 'yes' ? [{ label: 'Aanvullend pensioen / lijfrente', value: 'Ja' }] : []),
            ...(pension.hasAnwProvision === 'yes' ? [{ label: 'Nabestaandenvoorziening / ANW-hiaat', value: 'Ja' }] : []),
          ],
        })
      }

      // Woning
      if (housing?.address || housing?.mortgageProvider || housing?.mortgageNumber) {
        // Hypotheekverstrekker: nieuw veld (V37) met fallback naar legacy mortgage
        const hypotheekNaam = housing.mortgageProvider && housing.mortgageProvider !== 'Anders'
          ? housing.mortgageProvider
          : housing.mortgageNotes || housing.mortgage || 'Niet opgegeven'

        const items: { label: string; value: string }[] = [
          { label: 'Adres', value: housing.address || 'Niet opgegeven' },
          { label: 'Eigendomssituatie', value: housing.landlord || 'Niet opgegeven' },
          { label: 'Hypotheekverstrekker', value: hypotheekNaam },
        ]
        if (housing.mortgageNumber)
          items.push({ label: 'Hypotheeknummer', value: housing.mortgageNumber })
        if (housing.mortgagePhone || housing.mortgageEmail)
          items.push({ label: 'Contact bank', value: [housing.mortgagePhone, housing.mortgageEmail].filter(Boolean).join(' / ') })
        if (housing.hasOrv === 'yes') {
          items.push({ label: 'Overlijdensrisicoverzekering', value: 'Ja' })
          if (housing.orvInsurer)  items.push({ label: 'ORV verzekeraar', value: housing.orvInsurer })
          if (housing.orvPolicyNr) items.push({ label: 'ORV polisnummer', value: housing.orvPolicyNr })
          if (housing.orvContact)  items.push({ label: 'ORV contactgegevens', value: housing.orvContact })
        } else if (housing.hasOrv === 'unknown') {
          items.push({ label: 'Overlijdensrisicoverzekering', value: 'Onbekend - controleer bij hypotheekverstrekker' })
        }
        items.push({ label: 'Reservesleutels', value: housing.keys || 'Niet opgegeven' })
        if (housing.docsLocation)
          items.push({ label: 'Documenten aanwezig bij', value: housing.docsLocation })

        built.push({ title: 'Woning', icon: <Building className="h-4 w-4" />, items })
      }

      // Huisdieren
      if (answers.hasPets && pets?.pets?.length) {
        built.push({
          title: 'Huisdieren',
          icon: <Heart className="h-4 w-4" />,
          items: pets.pets.map(p => ({ label: `${p.name} (${p.species})`, value: p.caretaker ? `Verzorger: ${p.caretaker}` : 'Verzorger niet opgegeven' })),
        })
      }

      // V41-P2: Digitale toegang (telefoon/computer locaties)
      if (hasPhone || hasPhoneAccess || hasComputerAccess || hasPassMgr || hasAuth) {
        const digitalAccessItems: { label: string; value: string }[] = []
        if (hasPhone) {
          const brand = [digitalEstate!.phoneBrand, digitalEstate!.phoneModel].filter(Boolean).join(' ')
          digitalAccessItems.push({ label: 'Telefoon', value: brand || 'Aanwezig' })
          if (digitalEstate!.phoneProvider) digitalAccessItems.push({ label: 'Provider', value: digitalEstate!.phoneProvider })
        }
        if (hasPhoneAccess) digitalAccessItems.push({ label: 'Telefooncode te vinden', value: digitalEstate!.unlockCodeLocation })
        if (hasComputerAccess) digitalAccessItems.push({ label: 'Computertoegang te vinden', value: digitalEstate!.computerAccessLocation })
        if (hasPassMgr) {
          const pmNames: Record<string,string> = { '1password': '1Password', apple: 'Apple Wachtwoorden', google: 'Google Password Manager', bitwarden: 'Bitwarden' }
          const pmName = pmNames[digitalEstate!.passwordManagerApp] || digitalEstate!.passwordManagerApp
          digitalAccessItems.push({ label: 'Wachtwoordmanager', value: pmName })
          if (digitalEstate!.passwordManagerLocation) digitalAccessItems.push({ label: 'Locatie hoofdwachtwoord', value: digitalEstate!.passwordManagerLocation })
        }
        if (hasAuth) {
          const authNames: Record<string,string> = { microsoft: 'Microsoft Authenticator', google: 'Google Authenticator', apple: 'Apple' }
          const authName = authNames[digitalEstate!.authenticatorApp] || digitalEstate!.authenticatorApp
          digitalAccessItems.push({ label: 'Authenticator-app', value: authName })
        }
        built.push({ title: 'Digitale toegang', icon: <Smartphone className="h-4 w-4" />, items: digitalAccessItems })
      }

      // Digitale instructies
      if (notarial?.digitaleAccounts?.length) {
        built.push({
          title: 'Digitale accounts',
          icon: <Smartphone className="h-4 w-4" />,
          items: notarial.digitaleAccounts.map(d => ({ label: d.platform, value: d.actie })),
        })
      }

      // Medische wensen samenvatting
      if (medical) {
        built.push({
          title: 'Medische wensen',
          icon: <Heart className="h-4 w-4" />,
          items: [
            { label: 'Reanimatie', value: medical.resuscitation === 'yes' ? 'Ja' : medical.resuscitation === 'no' ? 'Niet reanimeren (DNAR)' : 'Bespreek met arts' },
            { label: 'Wilsverklaring', value: medical.livingWillExists === 'yes' ? 'Ja - ' + (medical.livingWillLocation || 'locatie onbekend') : medical.livingWillExists === 'pending' ? 'In voorbereiding' : 'Nee' },
            { label: 'Medisch vertegenwoordiger', value: [medical.proxyDecisionMaker, medical.proxySource].filter(Boolean).join(' - ') || 'Niet opgegeven' },
            ...(medical.proxyPhone ? [{ label: 'Telefoon vertegenwoordiger', value: medical.proxyPhone }] : []),
            ...(medical.proxyEmail ? [{ label: 'E-mail vertegenwoordiger', value: medical.proxyEmail }] : []),
          ],
        })
      }

      // Uitvaartwensen samenvatting - inclusief uitvaartverzekering (V37)
      if (funeral) {
        const funeralItems: { label: string; value: string }[] = [
          { label: 'Type', value: funeral.type === 'burial' ? 'Begraving' : funeral.type === 'cremation' ? 'Crematie' : funeral.type === 'natural' ? 'Natuurbegraving' : 'Geen voorkeur' },
          { label: 'Uitvaartverzorger', value: funeral.funeralHome || 'Niet opgegeven' },
          ...(funeral.funeralHomeContact ? [{ label: 'Contactpersoon', value: funeral.funeralHomeContact }] : []),
          ...(funeral.funeralHomePhone   ? [{ label: 'Telefoon',       value: funeral.funeralHomePhone   }] : []),
          ...(funeral.funeralHomeNotes   ? [{ label: 'Toelichting',    value: funeral.funeralHomeNotes   }] : []),
        ].filter(i => i.value)

        // V37: Uitvaartverzekering(en) ophalen uit financiële module
        const uitvaartPolissen = (insuranceData as InsuranceData | null)?.insurances?.filter(
          (ins: { type: string }) => ins.type === 'Uitvaartverzekering'
        ) ?? []

        if (uitvaartPolissen.length > 0) {
          uitvaartPolissen.forEach((polis, idx) => {
            const prefix = uitvaartPolissen.length > 1 ? `Polis ${idx + 1}: ` : ''
            const verzekeraar = polis.insurer === 'Anders' ? (polis.insurerOther || 'Onbekend') : (polis.insurer || 'Onbekend')
            funeralItems.push({ label: `${prefix}Uitvaartverzekering`, value: verzekeraar })
            if (polis.policyNumber) funeralItems.push({ label: `${prefix}Polisnummer`, value: polis.policyNumber })
            if (polis.uitvaartType) funeralItems.push({
              label: `${prefix}Type polis`,
              value: polis.uitvaartType === 'natura' ? 'Natura (verzekeraar regelt)' :
                     polis.uitvaartType === 'kapitaal' ? 'Kapitaal (bedrag uitgekeerd)' :
                     polis.uitvaartType === 'combinatie' ? 'Combinatie' : 'Onbekend',
            })
            if (polis.docsAvailable) funeralItems.push({
              label: `${prefix}Polisdocument`,
              value: polis.docsAvailable === 'yes' ? 'Aanwezig' : polis.docsAvailable === 'no' ? 'Niet aanwezig' : 'Onbekend',
            })
            if (polis.notes) funeralItems.push({ label: `${prefix}Notities`, value: polis.notes })
          })
        }

        built.push({
          title: 'Uitvaartwensen',
          icon: <Heart className="h-4 w-4" />,
          items: funeralItems,
        })
      }

      // Beleggingen - alleen tonen bij Ja (V39)
      const typedInvest = investData as InvestmentsData | null
      if (typedInvest?.hasInvestments === 'yes' && typedInvest.investments?.length > 0) {
        const invItems: { label: string; value: string }[] = []
        typedInvest.investments.forEach((inv, idx) => {
          const prefix = typedInvest.investments.length > 1 ? `${idx + 1}. ` : ''
          const broker = inv.brokerName === 'Anders' ? (inv.brokerNameOther || 'Onbekend') : (inv.brokerName || 'Onbekend')
          invItems.push({ label: `${prefix}Broker / platform`, value: broker })
          if (inv.investmentType) invItems.push({ label: `${prefix}Type`, value: inv.investmentType })
          if (inv.isCrypto) {
            const platform = inv.cryptoPlatform === 'Anders' ? (inv.cryptoPlatformOther || 'Onbekend') : inv.cryptoPlatform
            invItems.push({ label: `${prefix}Crypto platform`, value: platform })
          }
        })
        built.push({ title: 'Beleggingen & aandelen', icon: <CreditCard className="h-4 w-4" />, items: invItems })
      }

      // Extra locaties (V38)
      const extraLocs = (extraLocData as { hasExtraLocations?: string; locations?: import('@/modules/wizard/steps/NewStep06ExtraLocations').ExtraLocationEntry[] } | null)
      if (extraLocs?.locations && extraLocs.locations.length > 0) {
        extraLocs.locations.forEach((loc, idx) => {
          const typeLabel = {
            garagebox: 'Garagebox', opslagbox: 'Opslagbox', 'self-storage': 'Self-storage',
            bedrijfspand: 'Bedrijfspand', loods: 'Loods', werkplaats: 'Werkplaats',
            perceel: 'Perceel grond', anders: loc.typeOther || 'Anders',
          }[loc.type] ?? loc.type

          const items: { label: string; value: string }[] = [
            { label: 'Type', value: typeLabel },
            { label: 'Eigendomsvorm', value: loc.ownership === 'koop' ? 'Koop / eigendom' : loc.ownership === 'huur' ? 'Huur' : loc.ownership },
          ]
          // V42: gestructureerd adres; legacy fallback op loc.address
          const locAdres = loc.street
            ? [loc.street + (loc.houseNumber ? ' ' + loc.houseNumber : '') + (loc.houseNumberAddition ? loc.houseNumberAddition : ''),
               [loc.postalCode, loc.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
            : loc.address
          if (locAdres) items.push({ label: 'Adres', value: locAdres })
          if (loc.ownership === 'koop') {
            const hyp = loc.mortgageProvider === 'Anders' ? (loc.mortgageProviderOther || '') : loc.mortgageProvider
            if (hyp) items.push({ label: 'Hypotheekverstrekker', value: hyp })
            if (loc.mortgageNumber) items.push({ label: 'Hypotheeknummer', value: loc.mortgageNumber })
            if (loc.hasOrv === 'yes') {
              items.push({ label: 'ORV', value: 'Ja' })
              if (loc.orvInsurer) items.push({ label: 'ORV verzekeraar', value: loc.orvInsurer })
              if (loc.orvPolicyNr) items.push({ label: 'ORV polisnummer', value: loc.orvPolicyNr })
            } else if (loc.hasOrv === 'unknown') {
              items.push({ label: 'ORV', value: 'Onbekend' })
            }
          }
          if (loc.ownership === 'huur') {
            if (loc.landlord) items.push({ label: 'Verhuurder', value: loc.landlord })
            if (loc.contractNr) items.push({ label: 'Contractnummer', value: loc.contractNr })
            if (loc.landlordContact) items.push({ label: 'Contact verhuurder', value: loc.landlordContact })
          }
          if (loc.keys) items.push({ label: 'Sleutels bij', value: loc.keys })
          if (loc.docsLocation) items.push({ label: 'Documenten bij', value: loc.docsLocation })
          if (loc.notes) items.push({ label: 'Bijzonderheden', value: loc.notes })

          built.push({
            title: `Extra locatie ${extraLocs.locations!.length > 1 ? idx + 1 : ''} - ${typeLabel}`.replace(/ -\s*$/, ''),
            icon: <Building className="h-4 w-4" />,
            items,
          })
        })
      }

      // Persoonlijke boodschap
      if (notarial?.persoonlijkeBoodschap) {
        built.push({
          title: 'Persoonlijke boodschap',
          icon: <Heart className="h-4 w-4" />,
          items: [{ label: 'Boodschap', value: notarial.persoonlijkeBoodschap.slice(0, 200) + (notarial.persoonlijkeBoodschap.length > 200 ? '...' : '') }],
        })
      }

      // ── Telefoon & digitale toegang - checklist (V41) ─────────────────────────
      if (hasPhone || hasAuth || hasPassMgr) {
        const urgentItems: { label: string; value: string }[] = []
        const normalItems: { label: string; value: string }[] = []

        if (hasPhone) {
          urgentItems.push(
            { label: '⚠️ Urgent', value: 'Telefoon van overledene veiligstellen' },
            { label: '⚠️ Urgent', value: 'Controleer toegang tot smartphone' },
          )
        }
        if (hasAuth) {
          urgentItems.push(
            { label: '⚠️ Urgent', value: 'Controleer authenticator-apps vóór accounts worden geblokkeerd' },
          )
        }
        urgentItems.push(
          { label: '⚠️ Urgent', value: 'Controleer toegang tot e-mailaccount' },
        )
        if (hasPassMgr) {
          normalItems.push(
            { label: 'Normaal', value: 'Wachtwoordmanager controleren' },
          )
        }
        normalItems.push(
          { label: 'Normaal', value: 'Digitale accounts inventariseren' },
          { label: 'Normaal', value: 'Digitale nalatenschap afhandelen' },
        )

        built.push({
          title: 'Digitale checklist',
          icon: <Smartphone className="h-4 w-4" />,
          items: [...urgentItems, ...normalItems],
        })
      }

      setSections(built)
      // Exportdata voor professioneel dossier
      const exportSections: ExportSection[] = built.map(sec => ({
        title: sec.title,
        items: sec.items.map(item => ({ label: item.label, value: item.value })),
      }))
      setExportData({
        type: 'nabestaanden', firstName, exportDate: new Date().toLocaleDateString('nl-NL', { day:'2-digit', month:'2-digit', year:'numeric' }), version: 'V43',
        sections: exportSections,
        summaryItems: [
          'Neem contact op met de vertrouwenspersoon',
          'Informeer de uitvaartverzorger',
          'Controleer bankrekeningen en verzekeringen',
          'Raadpleeg de notaris voor nalatenschap',
          'Regels digitale accounts en abonnementen',
        ],
      })
      setIsLoading(false)
    }
    load()
  }, [userId, answers])

  async function handleDownloadDocx() {
    if (!exportData) return
    const { generateDocx, downloadDocx } = await import('@/lib/export/generateDocx')
    const blob = await generateDocx(exportData)
    downloadDocx(blob, `WilsApp-Nabestaandendossier-${firstName || 'export'}.docx`)
  }

  async function handleDownloadPdf() {
    if (!exportData) return
    const { generatePdf, downloadPdf } = await import('@/lib/export/generatePdf')
    const blob = await generatePdf(exportData)
    downloadPdf(blob, `WilsApp-Nabestaandendossier-${firstName || 'export'}.pdf`)
  }


  function handlePrint() {
    setGenerated(true)
    setTimeout(() => window.print(), 300)
  }

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <button onClick={() => navigate(DOSSIER_OVERVIEW)} className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors">
        <ArrowLeft className="h-4 w-4" />Terug naar dossiers
      </button>

      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-700 flex-shrink-0">
          <Users className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-ink">Nabestaandendossier</h1>
          <p className="text-xs text-ink-4">Praktische hulp voor uw naasten na uw overlijden</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={handleDownloadDocx} icon={<Download className="h-4 w-4" />}>Word (.docx)</Button>
        <Button onClick={handleDownloadPdf} variant="secondary" icon={<Download className="h-4 w-4" />}>PDF</Button>
        <Button variant="secondary" onClick={handlePrint} icon={<Printer className="h-4 w-4" />}>Printen</Button>
      </div>

      {generated && (
        <div className="text-xs text-sage-700 bg-sage-50 border border-sage-200 rounded-xl px-4 py-2.5">
          ✓ Dossier gegenereerd. In de productieversie kunt u dit ook als PDF opslaan of per e-mail versturen.
        </div>
      )}

      {isLoading ? <p className="text-sm text-ink-4">Laden...</p> : (
        <>
          {sections.length === 0 && (
            <Card>
              <p className="text-sm text-ink-3 text-center py-4">Vul eerst de wizard in om een nabestaandendossier te genereren.</p>
              <Button fullWidth onClick={() => navigate('/wizard')}>Naar de wizard</Button>
            </Card>
          )}
          {sections.map((sec, si) => (
            <Card key={si}>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-sage-50 text-sage-600 flex-shrink-0">
                  {sec.icon}
                </div>
                <h2 className="text-sm font-semibold text-ink">{sec.title}</h2>
              </div>
              <div className="flex flex-col divide-y divide-sand-200">
                {sec.items.map((item, ii) => {
                  const digits = item.value.replace(/[^\d]/g, '')
                  const isPhone = digits.length >= 7
                  return (
                    <div key={ii} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <span className="text-xs font-medium text-ink-3 block">{item.label}</span>
                        <span className="text-xs text-ink leading-relaxed break-words">{item.value}</span>
                      </div>
                      {isPhone && (
                        <a href={`tel:${item.value.replace(/\s/g, '')}`}
                          aria-label={`Bel ${item.value}`}
                          className="flex items-center gap-1.5 rounded-lg bg-indigo-500 text-white px-3 py-1.5 text-xs font-medium hover:bg-indigo-600 active:bg-indigo-700 transition-colors flex-shrink-0">
                          <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                          Bellen
                        </a>
                      )}
                    </div>
                  )
                })}
              </div>
            </Card>
          ))}
        </>
      )}

      <div className="rounded-xl bg-sand border border-sand-300 px-4 py-4 flex flex-col gap-3 text-xs text-ink-4 leading-relaxed">
        <p><strong className="text-ink-3">Bewaar dit document veilig.</strong> Informeer uw vertrouwenspersoon waar dit dossier te vinden is.</p>
        <div className="flex flex-col gap-1.5 pt-2 border-t border-sand-300">
          <p className="text-ink-3 font-medium">Officiële instanties voor vervolgstappen:</p>
          {[
            { label: 'SVB - nabestaandenuitkering (ANW) aanvragen', url: 'https://www.svb.nl/nl/anw' },
            { label: 'SVB - AOW-pensioen afmelden bij overlijden', url: 'https://www.svb.nl/nl/aow/overlijden' },
            { label: 'Belastingdienst - aangifte erfbelasting (binnen 8 maanden)', url: 'https://www.belastingdienst.nl/wps/wcm/connect/nl/erfbelasting/content/erfbelasting_wanneer_doe_ik_aangifte' },
            { label: 'Notaris.nl - verklaring van erfrecht aanvragen', url: 'https://www.notaris.nl/notariele-producten/verklaring-van-erfrecht' },
            { label: 'Rijksoverheid - overzicht van alles regelen na overlijden', url: 'https://www.rijksoverheid.nl/onderwerpen/overlijden/regelen-na-overlijden' },
            { label: 'Belastingdienst - toeslagen controleren na overlijden', url: 'https://www.toeslagen.nl/' },
            { label: 'Rechtspraak - erfenis aanvaarden of verwerpen', url: 'https://www.rechtspraak.nl/onderwerpen/erfenis' },
          ].map(item => (
            <a key={item.url} href={item.url} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-xs text-ink-3 underline underline-offset-2 hover:text-ink transition-colors">
              <span className="flex-shrink-0">→</span>
              {item.label}
            </a>
          ))}
        </div>
        <p className="pt-1 border-t border-sand-300 text-ink-4 leading-relaxed">
          Dit dossier bevat door de gebruiker vastgelegde aanwijzingen en wensen. WilsApp is geen officiële bron. Neem voor overlijdensregistratie, juridische geldigheid of uitkeringen contact op met de bevoegde instanties.
        </p>
      </div>
    </div>
  )
}
